import { RouteLocationRaw } from "vue-router";
import { InvoiceDto } from "@/core/features/invoice/invoice.dto";
import { CertificationHasInvoiceDto } from "@/core/features/certifications-have-invoices/certification-has-invoice.dto";
import { roundCurrency } from "@/modules/invoices/utils/invoice-amount.utils";
import { resolveInvoiceDeliveryAmount } from "@/modules/invoices/utils/invoice-delivery-amount.utils";

export function getInvoiceAllocatedTotal(invoice: InvoiceDto): number {
  return (invoice.certificationHasInvoices ?? []).reduce((sum, link) => sum + (link.allocatedAmount ?? 0), 0);
}

export function getInvoiceUnallocatedAmount(invoice: InvoiceDto): number {
  const deliveryAmount = resolveInvoiceDeliveryAmount(invoice.amount, invoice.totalAmount) ?? 0;
  return roundCurrency(deliveryAmount - getInvoiceAllocatedTotal(invoice));
}

export function hasInvoicePendingAllocation(invoice: InvoiceDto): boolean {
  const deliveryAmount = resolveInvoiceDeliveryAmount(invoice.amount, invoice.totalAmount) ?? 0;
  const unallocatedAmount = getInvoiceUnallocatedAmount(invoice);

  if (deliveryAmount > 0) {
    return unallocatedAmount > 0;
  }

  if (deliveryAmount < 0) {
    return unallocatedAmount < 0;
  }

  return false;
}

export function isInvoiceAvailableForCertification(invoice: InvoiceDto, certificationId: string): boolean {
  const isAlreadyLinked = invoice.certificationHasInvoices?.some((link) => link.certificationId === certificationId);

  return !isAlreadyLinked && hasInvoicePendingAllocation(invoice);
}

export function getInvoiceAllocatedAmountForCertification(
  invoice: InvoiceDto,
  certificationId: string
): number | undefined {
  return invoice.certificationHasInvoices?.find((link) => link.certificationId === certificationId)?.allocatedAmount;
}

export function getInvoiceCertificationCodes(invoice: InvoiceDto): string {
  const codes = (invoice.certificationHasInvoices ?? [])
    .map((link) => link.certification?.code)
    .filter((code): code is string => !!code);

  return codes.length ? codes.join(", ") : "-";
}

export function getInvoicePrimaryCertification(invoice: InvoiceDto) {
  return invoice.certificationHasInvoices?.[0]?.certification;
}

export function getInvoiceCompanyName(invoice: InvoiceDto): string {
  const companyName =
    invoice.company?.name?.trim() || getInvoicePrimaryCertification(invoice)?.dossier?.company?.name?.trim();
  return companyName || "-";
}

export function getInvoiceCertificationLinks(invoice: InvoiceDto): CertificationHasInvoiceDto[] {
  return (invoice.certificationHasInvoices ?? []).filter(
    (link) => !!link.certification?.code && !!link.certification?.status
  );
}

export function buildInvoiceDeliveryCertificationRoute(link: CertificationHasInvoiceDto): RouteLocationRaw {
  return {
    name: "Certifications' Detail's invoice",
    params: { id: link.certificationId, selectedId: link.invoiceId },
    query: { sidebar: "true" },
  };
}

export function getInvoiceUnallocatedWarningMessage(
  invoice: InvoiceDto,
  formatCurrency: (amount: number) => string
): string | null {
  const unallocatedAmount = getInvoiceUnallocatedAmount(invoice);
  if (!hasInvoicePendingAllocation(invoice)) {
    return null;
  }

  return `Faltan: ${formatCurrency(unallocatedAmount)} por incluir en alguna certificación`;
}
